/* eslint-disable no-nested-ternary */
import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChangeLog,
  Pagination,
  Response,
  TransactionWarning,
  TransactionWarningActionEnum,
} from '@ui/src';
import {
  DocumentComment,
  OngoingMonitring,
  OngoingCheckingStatus,
  CorporateQuestionnarieFormValues,
  Questionnarie,
  CustomerScore,
  CustomerScoreQueuedProps,
  CustomerScoreUpdateStatus,
} from '../../../customers/types';
import {
  FormValues,
  NewApplicationStatus,
  Match,
  Comment,
  NewApplicationCorporateInfo,
  NewApplicationCorporateSteps,
  ApplicationType,
  SystemLogs,
  ApplicationComment,
  NewApplicationChildrenInfo,
  DocumentFormValues,
  ApplicationDocumentType,
  ApplicationDocument,
  ApplicationDocumentStatus,
  NewApplicationStepStatus,
  ApplicationAgreement,
  NewApplicationListItem,
  DictionaryItem,
  AccountsCurrencies,
  AccountsFlows,
  ExpectedFundsOrigins,
} from '../../types';
import { applicationsApi } from '../../store/services';
import { clientsDocumentsApi } from '../../../../store/documents/services';
import {
  getStepOneStatus,
  getStepTwoStatus,
  getStepThreeStatus,
  getStepFourStatus,
  getStepFiveStatus,
  getStepSixStatus,
  getStepSevenStatus,
} from '../helpers/update-application-step-statuses';

import ROUTES from '../../../../routes';

export interface NewApplicationCorporateInfoState {
  isLoading: boolean;
  applicationId?: string;
  comment?: Comment;
  sendCommentInProgress?: boolean;
  newChatMessageRecieved?: boolean;
  chatMessageHintIsVisible?: boolean;
  requestInProgress: boolean;
  formValues?: FormValues;
  documentSearchText?: string;
  selectedDocumentsCounts: number;
  newApplicationInfo?: NewApplicationCorporateInfo;
  documents?: ApplicationDocument[];
  hintVisible?: boolean;
  stepsStatus?: NewApplicationCorporateSteps;
  systemLogs?: SystemLogs[];
  documentFormValues: DocumentFormValues;
  onAddSuccess?: boolean;
  fileUploadInProgress?: boolean;
  documentName?: string;
  documentSubmitInProgress?: boolean;
  groupToMove?: ApplicationDocumentType;
  documentMoveInProgress?: boolean;
  deleteSuccess?: boolean;
  deleteInProgress?: boolean;
  documentId?: string;
  documentIsLoading?: boolean;
  document?: ApplicationDocument;
  agreements?: ApplicationAgreement[];
  mainRepresentativeApplications?: NewApplicationChildrenInfo[];
  companyOfficialsApplications?: NewApplicationChildrenInfo[];
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  questionnarie?: CorporateQuestionnarieFormValues;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  riskScore?: CustomerScore;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  lastCalculatingAml?: CustomerScore;
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  sourceOfWealth?: DictionaryItem[];
  busniessActivities?: DictionaryItem[];
  customerMainCleints?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  typeOfAccounts?: DictionaryItem[];
  accountsCurrencies?: AccountsCurrencies[];
  questionnarieId?: string;
  accountsFlows?: AccountsFlows[];
  expectedFundsOrigins?: ExpectedFundsOrigins[];
}

const initialState: NewApplicationCorporateInfoState = {
  isLoading: false,
  requestInProgress: false,
  documentSearchText: '',
  selectedDocumentsCounts: 0,
  hintVisible: false,
  documentFormValues: {},
};

export const newAppliationCorporateInfoSlice = createSlice({
  name: 'newAppliationCorporateInfo',
  initialState,
  reducers: {
    clearCofAndMRState: (state) => {
      state.mainRepresentativeApplications = undefined;
      state.companyOfficialsApplications = undefined;
    },
    updateStepsStatus: (
      state,
      action: PayloadAction<NewApplicationCorporateSteps>,
    ) => {
      state.stepsStatus = action.payload;
    },
    updateMainRepresentativeValues: (
      state,
      action: PayloadAction<NewApplicationChildrenInfo[]>,
    ) => {
      state.newApplicationInfo = {
        ...state.newApplicationInfo,
        mainRepresentative: action.payload,
      } as NewApplicationCorporateInfo;
    },
    updateCompanyOfficialValues: (
      state,
      action: PayloadAction<NewApplicationChildrenInfo[]>,
    ) => {
      state.newApplicationInfo = {
        ...state.newApplicationInfo,
        companyOfficial: action.payload,
      } as NewApplicationCorporateInfo;
    },
    setMatchSelected: (
      state,
      action: PayloadAction<{ id: string; selected: boolean }>,
    ) => {
      const nextInfo = { ...state.newApplicationInfo };

      nextInfo.similarApplications = {
        edges: nextInfo.similarApplications?.edges.map((item) => {
          const result = { ...item };
          if (result.node.id === action.payload.id) {
            result.node.selected = action.payload.selected;
          }

          return result;
        }) as { node: Match }[],
      };

      state.newApplicationInfo = nextInfo as NewApplicationCorporateInfo;
    },
    checkChatMessages: (state) => {
      state.newChatMessageRecieved = true;
      state.chatMessageHintIsVisible = true;
    },
    setDocumentSearchText: (state, action: PayloadAction<string>) => {
      state.documentSearchText = action.payload;
    },
    rejectApplication: (state) => {
      const nextInfo = { ...state.newApplicationInfo };

      nextInfo.status = NewApplicationStatus.DECLINED;

      state.newApplicationInfo = nextInfo as NewApplicationCorporateInfo;
    },
    closeChatMessageHint: (state) => {
      state.chatMessageHintIsVisible = false;
    },
    setHintVisible: (state, action: PayloadAction<boolean>) => {
      state.hintVisible = action.payload;
    },
    onSelectAllGroup: (state, action: PayloadAction<boolean>) => {
      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };

        result.isSelected = action.payload;

        return result;
      }) as ApplicationDocument[];

      state.documents = nextDocuments;
      state.selectedDocumentsCounts = action.payload ? nextDocuments.length : 0;
    },
    onGroupSelected: (
      state,
      action: PayloadAction<ApplicationDocumentType>,
    ) => {
      state.documentFormValues = {
        ...state.documentFormValues,
        documentGroup: action.payload,
      };
    },
    onSelectDocument: (
      state,
      action: PayloadAction<{
        selected: boolean;
        id: string;
      }>,
    ) => {
      let selectedCount = state.selectedDocumentsCounts;

      if (state.documents) {
        const nextSelectedDocuments = state.documents?.map((item) => {
          const result = { ...item };

          if (item.id === action.payload.id) {
            result.isSelected = action.payload.selected;

            if (action.payload.selected) {
              selectedCount += 1;
            } else {
              selectedCount -= 1;
            }
          }

          return result;
        });

        state.documents = nextSelectedDocuments;
        state.selectedDocumentsCounts = selectedCount;
      }
    },
    onDocumentMove: (state, action: PayloadAction<string>) => {
      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };

        if (result.isSelected) {
          result.isSelected = false;
          result.documentCategoryId = action.payload;
        }

        if (state.document && result.id === state.document.id) {
          result.documentCategoryId = action.payload;
        }

        return result;
      });

      state.documents = nextDocuments;

      if (state.document) {
        const nextDocument = { ...state.document };
        nextDocument.documentCategoryId = action.payload;

        state.document = nextDocument;
      }

      state.selectedDocumentsCounts = 0;
    },
    deleteIsSuccess: (state, action: PayloadAction<ApplicationDocument[]>) => {
      state.documents = action.payload;
      state.selectedDocumentsCounts = 0;
    },
    clearDocumentSidepanelState: (state) => {
      state.documentId = undefined;
      state.document = undefined;
    },
    clearNewApplicationInfoState: (state) => {
      state.isLoading = false;
      state.applicationId = undefined;
      state.newApplicationInfo = undefined;
      state.documentSearchText = '';
      state.chatMessageHintIsVisible = undefined;
      state.newChatMessageRecieved = undefined;
      state.groupToMove = undefined;
      state.selectedDocumentsCounts = 0;
      state.documents = undefined;
      state.agreements = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.ongoingMonitoring = undefined;
      state.amlUpdateStatus = undefined;
      state.riskScore = undefined;
      state.lastCalculatingAml = undefined;
      state.questionnarieId = undefined;
      state.accountsCurrencies = undefined;
      state.accountsFlows = undefined;
      state.expectedFundsOrigins = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingWarning.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TransactionWarning }> & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        if (state.newApplicationInfo) {
          const nextWarnings = state.ongoingMonitoring?.map((item) => {
            const result = { ...item };

            if (item.id === action.meta.arg.originalArgs.id) {
              const nextGroupWarnings = item.warnings?.edges?.map((warning) => {
                const warningResult = { ...warning };

                if (
                  warningResult.node.id ===
                  action.meta.arg.originalArgs.warningId
                ) {
                  warningResult.node.blacklistDoc =
                    action.payload.data.blacklistDoc;
                  warningResult.node.blacklistItem =
                    action.payload.data.blacklistItem;
                }

                return warningResult;
              });

              result.warnings = {
                edges: nextGroupWarnings,
                pageInfo: result.warnings.pageInfo,
              };
            }

            return result;
          });

          state.ongoingMonitoring = nextWarnings;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationLastCalculatingScore
        .matchFulfilled,
      (state, action: PayloadAction<Response<CustomerScore>>) => {
        state.lastCalculatingAml = action.payload.data.edges[0].node;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getRelationShipToCustomerDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.relationshipToTheCustomerDictionary =
          action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getBusinessActivitiesDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        const nextBusinessActivities = action.payload.data.edges?.map(
          (item) => item.node,
        );

        nextBusinessActivities.unshift({
          value: 'None of the listed activities',
          id: 'NONE',
          dictionaryId: nextBusinessActivities[0].id,
          createdAt: nextBusinessActivities[0].createdAt,
          updatedAt: nextBusinessActivities[0].updatedAt,
        });

        state.busniessActivities = nextBusinessActivities;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getSourceOfWealthForCorporateDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomersMainClientsDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerMainCleints = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getSalesChannelsDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.salesChannels = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomerSourceOfFunds.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfFunds = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomerSourceOfWealth.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getTypeOfAccountsDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.typeOfAccounts = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getAnnualIncomeDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.annualIncomes = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getAccountsCurrencies.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsCurrencies>>) => {
        state.accountsCurrencies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        let nextAccountsCurrencies: AccountsCurrencies[] = [];

        if (state.accountsCurrencies) {
          nextAccountsCurrencies = ([] as AccountsCurrencies[]).concat(
            state.accountsCurrencies,
          );
        }

        nextAccountsCurrencies.push(action.payload.data);

        state.accountsCurrencies = nextAccountsCurrencies;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getAccountsFlows.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsFlows>>) => {
        state.accountsFlows = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        let nextAccountsFlows: AccountsFlows[] = [];

        if (state.accountsFlows) {
          nextAccountsFlows = ([] as AccountsFlows[]).concat(
            state.accountsFlows,
          );
        }

        nextAccountsFlows.push(action.payload.data);

        state.accountsFlows = nextAccountsFlows;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getExpectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<Response<ExpectedFundsOrigins>>) => {
        state.expectedFundsOrigins = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createExpectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        let nextExpectedFundsOrigins: ExpectedFundsOrigins[] = [];

        if (state.expectedFundsOrigins) {
          nextExpectedFundsOrigins = ([] as ExpectedFundsOrigins[]).concat(
            state.expectedFundsOrigins,
          );
        }

        nextExpectedFundsOrigins.push(action.payload.data);

        state.expectedFundsOrigins = nextExpectedFundsOrigins;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateExpectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.map(
            (item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            },
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteExptectedFundsOrigins.matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createApplicationRiskScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;
        localStorage.setItem('applicationAmlUpdateId', action.payload.data.id);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationRiskScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScore }>) => {
        if (
          state.newApplicationInfo &&
          state.newApplicationInfo.id === action?.payload?.data?.applicationId
        ) {
          state.riskScore = action.payload.data;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAmlUpdatingItem.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;

        if (action.payload.data.status === CustomerScoreUpdateStatus.FAIL) {
          localStorage.removeItem('applicationAmlUpdateId');
        }

        if (action.payload.data.status === CustomerScoreUpdateStatus.SUCCESS) {
          localStorage.removeItem('applicationAmlUpdateId');
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationCorporateInfo.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const nextInfo = action.payload.data;

        let mainRepresentative: NewApplicationChildrenInfo[] = [];
        let companyOfficials: NewApplicationChildrenInfo[] = [];

        state.applicationId = action.payload.data.id;

        if (action?.payload?.data?.questionnaire) {
          state.questionnarieId = action.payload.data?.questionnaire?.id;
        }

        if (nextInfo?.relatedApplications?.length) {
          const mainRepresentativeApplications =
            nextInfo?.relatedApplications?.filter(
              (item) =>
                item.companyOfficialType ===
                ApplicationType.MAIN_REPRESENTATIVE,
            );

          const companyOfficialsApplications =
            nextInfo?.relatedApplications?.filter(
              (item) =>
                item.companyOfficialType === ApplicationType.COMPANY_OFFICIAL,
            );

          if (mainRepresentativeApplications) {
            state.mainRepresentativeApplications =
              mainRepresentativeApplications;

            mainRepresentative = mainRepresentativeApplications;
          }

          if (companyOfficialsApplications) {
            state.companyOfficialsApplications = companyOfficialsApplications;

            companyOfficials = companyOfficialsApplications;
          }
        }

        const nextFormValues = {
          ...nextInfo,
          products: action.payload.data.products?.edges.map((item) => item),
        };

        const stepsStatus = {
          stepOne: getStepOneStatus(nextFormValues),
          stepTwo: getStepTwoStatus(nextFormValues),
          stepThree: getStepThreeStatus(nextFormValues),
          stepFour: getStepFourStatus(mainRepresentative),
          stepFive: getStepFiveStatus(companyOfficials),
          stepSix: NewApplicationStepStatus.AWAITING,
          stepSeven: getStepSevenStatus(),
        };

        const similarApplications = nextInfo?.similarApplications?.edges.map(
          (item) => ({
            node: {
              id: item.node.id,
              firstName: item.node.firstName || '',
              lastName: item.node.lastName || '',
              status: item.node.status,
              userId: item.node.userId || '',
              isClient: item.node.isClient,
              type: item.node.type,
              companyName: item.node.companyName || '',
              selected: Boolean(
                nextInfo.reviewedDuplicates.find(
                  (duplicate) => item.node.id === duplicate,
                ),
              ),
            },
          }),
        );

        state.newApplicationInfo = {
          ...nextInfo,
          similarApplications: {
            edges: similarApplications as { node: Match }[],
          },
        };
        state.stepsStatus = stepsStatus;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationCorporateOrganization
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const nextInfo = { ...state.newApplicationInfo };

        nextInfo.organizationId = action.payload.data.organizationId;

        state.newApplicationInfo = nextInfo as NewApplicationCorporateInfo;
      },
    );

    builder.addMatcher(
      applicationsApi.endpoints.getApplicationWarningGroupWithPagination
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<TransactionWarning>> & {
          meta: {
            arg: {
              originalArgs: {
                warningGroupId: string;
              };
            };
          };
        },
      ) => {
        if (state.ongoingMonitoring) {
          const nextWarningsGroups = state.ongoingMonitoring?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningGroupId) {
              const warningResult = { ...result.warnings };

              warningResult.pageInfo = action.payload.data.pageInfo;

              const currentWarnings = result.warnings.edges?.map(
                (warning) => warning.node,
              );

              const nextWarnings = ([] as TransactionWarning[]).concat(
                currentWarnings,
              );

              action?.payload?.data?.edges?.forEach((warning) =>
                nextWarnings.push(warning.node),
              );

              warningResult.edges = nextWarnings?.map((warning) => ({
                node: warning,
              }));

              result.warnings = warningResult;
            }

            return result;
          });

          state.ongoingMonitoring = nextWarningsGroups;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.submitApplication.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        if (state.newApplicationInfo) {
          state.newApplicationInfo = {
            ...state.newApplicationInfo,
            status: action.payload.data.status,
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationQuestionnarie.matchFulfilled,
      (state, action: PayloadAction<{ data: Questionnarie }>) => {
        if (state.newApplicationInfo && action?.payload?.data) {
          const { data } = action.payload;

          state.questionnarie = {
            typeOfBusinessLicence: data?.typeOfBusinessLicence || '',
            hmrcMlrNumber: data?.hmrcMlrNumber || '',
            hmrcRegistrationExpiryDate: data?.hmrcRegistrationExpiryDate || '',
            fcaNumber: data?.fcaNumber || '',
            icoNumber: data?.icoNumber || '',
            numberOfBranches: data?.numberOfBranches || '',
            numberOfAgents: data?.numberOfAgents || '',
            mlroDetails: data?.mlroDetails || '',
            complianceConsultantDetails:
              data?.complianceConsultantDetails || '',
            accountantDetails: data?.accountantDetails || '',
            itProviderDetails: data?.itProviderDetails || '',
            payoutPartnerDetails: data.payoutPartnerDetails || '',
            hasTransactionMonitoring: data?.hasTransactionMonitoring?.toString()
              ? data?.hasTransactionMonitoring === false
                ? 'no'
                : 'yes'
              : '',
            transactionMonitoringKind: data?.transactionMonitoringKind || '',
            hasScreening: data?.hasScreening?.toString()
              ? data?.hasScreening === false
                ? 'no'
                : 'yes'
              : '',
            screeningKind: data?.screeningKind || '',
            hasRegulatoryInvestigation:
              data?.hasRegulatoryInvestigation?.toString()
                ? data?.hasRegulatoryInvestigation === false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryInvestigationDetails:
              data?.regulatoryInvestigationDetails || '',
            visitedByAuthorities: data?.visitedByAuthorities || '',
            visitedByAuthoritiesDetails:
              data?.visitedByAuthoritiesDetails || '',
            hasOnboardCorporateClients:
              data?.hasOnboardCorporateClients?.toString()
                ? data?.hasOnboardCorporateClients === false
                  ? 'no'
                  : 'yes'
                : '',
            hasOnboardMsb: data?.hasOnboardMsb?.toString()
              ? data?.hasOnboardMsb === false
                ? 'no'
                : 'yes'
              : '',
            hasPaymentsForCharities: data?.hasPaymentsForCharities?.toString()
              ? data?.hasPaymentsForCharities === false
                ? 'no'
                : 'yes'
              : '',
            hasChangesInManagementOrOwnership:
              data?.hasChangesInManagementOrOwnership?.toString()
                ? data?.hasChangesInManagementOrOwnership === false
                  ? 'no'
                  : 'yes'
                : '',
            changesInManagementOrOwnershipDetails:
              data?.visitedByAuthoritiesDetails || '',
            hasWorkingWithAnyOtherIpsp:
              data?.hasWorkingWithAnyOtherIpsp?.toString()
                ? data?.hasWorkingWithAnyOtherIpsp === false
                  ? 'no'
                  : 'yes'
                : '',
            workingWithAnyOtherIpspDetails:
              data?.workingWithAnyOtherIpspDetails || '',
            hasAuditedByExternalCompliance:
              data?.hasAuditedByExternalCompliance?.toString()
                ? data?.hasAuditedByExternalCompliance === false
                  ? 'no'
                  : 'yes'
                : '',
            auditedByExternalComplianceDetails:
              data?.auditedByExternalComplianceDetails || '',
            contactPersonName: data?.contactPersonName || '',
            phoneNumber: data?.phoneNumber || '',
            faxNumber: data?.faxNumber || '',
            contactEmail: data?.contactEmail || '',
            legalEntityIdCode: data?.legalEntityIdCode || '',
            isPepOrAssociate:
              data?.isPepOrAssociate?.toString() &&
              data?.isPepOrAssociate?.toString() !== 'null'
                ? data?.isPepOrAssociate === false
                  ? 'no'
                  : 'yes'
                : '',
            nameOfPep: data?.nameOfPep || '',
            relToCustomerId: data?.relToCustomerId || '',
            relToPep: data?.relToPep || '',
            orgEmployerAndPosition: data?.orgEmployerAndPosition || '',
            positionHeldCountryId: data?.positionHeldCountryId || '',
            politicalFunctionPeriodFrom:
              data?.politicalFunctionPeriodFrom || '',
            politicalFunctionPeriodTo: data?.politicalFunctionPeriodTo || '',
            abilityMoveGovernmentFunds:
              data?.abilityMoveGovernmentFunds?.toString() &&
              data?.abilityMoveGovernmentFunds?.toString() !== 'null'
                ? data?.abilityMoveGovernmentFunds === false
                  ? 'no'
                  : 'yes'
                : '',
            abilityMoveGovernmentFundsDetails:
              data?.abilityMoveGovernmentFundsDetails || '',
            belongsToGroup:
              data?.belongsToGroup?.toString() &&
              data?.belongsToGroup?.toString() !== 'null'
                ? data?.belongsToGroup === false
                  ? 'no'
                  : 'yes'
                : '',
            parentCompanyName: data?.parentCompanyName || '',
            parentCompanyCountryId: data?.parentCompanyCountryId || '',
            subsidiariesInfo: data?.subsidiariesInfo || '',
            mainActivities: data?.mainActivities || '',
            groupWealthSourceIds: data?.groupWealthSourceIds || [],
            groupWealthSourceOther: data?.groupWealthSourceOther || '',
            estimatedTotalWealthEuro:
              data?.estimatedTotalWealthEuro ||
              data?.estimatedTotalWealthEuro?.toString() === '0'
                ? data?.estimatedTotalWealthEuro?.toString()
                : '',
            customerAppliesKycProcedures:
              data?.customerAppliesKycProcedures?.toString() &&
              data?.customerAppliesKycProcedures?.toString() !== 'null'
                ? data?.customerAppliesKycProcedures === false
                  ? 'no'
                  : 'yes'
                : '',
            hasBusinessActivity:
              data?.hasBusinessActivity?.toString() &&
              data?.hasBusinessActivity?.toString() !== 'null'
                ? data?.hasBusinessActivity === false
                  ? 'no'
                  : 'yes'
                : '',
            businessActivitiesIds:
              data?.hasBusinessActivity?.toString() &&
              data?.hasBusinessActivity?.toString() !== 'null' &&
              data?.hasBusinessActivity === false
                ? ['NONE']
                : data?.businessActivitiesIds,
            customersMainClientsIds: data?.customersMainClientsIds || [],
            customersMainClientsOther: data?.customersMainClientsOther || '',
            salesChannelsIds: data?.salesChannelsIds || [],
            salesChannelsOther: data?.salesChannelsOther || '',
            numberOfEmployees:
              data?.numberOfEmployees ||
              data?.numberOfEmployees?.toString() === '0'
                ? data?.numberOfEmployees.toString()
                : '',
            hasAccountsInOtherFinancialInstitutions:
              data?.hasAccountsInOtherFinancialInstitutions?.toString() &&
              data?.hasAccountsInOtherFinancialInstitutions?.toString() !==
                'null'
                ? data?.hasAccountsInOtherFinancialInstitutions === false
                  ? 'no'
                  : 'yes'
                : '',
            otherFinancialInstitutions: data?.otherFinancialInstitutions || '',
            sourceOfWealthIds: data?.sourceOfWealthIds || [],
            sourceOfWealthOther: data?.sourceOfWealthOther,
            sourcesOfFundsIds: data?.sourcesOfFundsIds || [],
            sourcesOfFundsLoanBusinessPartner:
              data?.sourcesOfFundsLoanBusinessPartner || '',
            sourcesOfFundsLoanCreditInstitution:
              data?.sourcesOfFundsLoanCreditInstitution || '',
            sourcesOfFundsOther: data?.sourcesOfFundsOther || '',
            estimatedTotalWealth:
              data?.estimatedTotalWealth ||
              data?.estimatedTotalWealth?.toString() === '0'
                ? data?.estimatedTotalWealth?.toString()
                : '',
            annualIncome:
              data?.annualIncome || data?.annualIncome?.toString() === '0'
                ? data?.annualIncome?.toString()
                : '',
            sourcesOfAnnualIncomes: Array.isArray(data?.sourcesOfAnnualIncomes)
              ? data?.sourcesOfAnnualIncomes
              : [],
            sourcesOfAnnualIncomesOther:
              data?.sourcesOfAnnualIncomesOther || '',
            accountTypeIds: data?.accountTypeIds || [],
            tradingName: data?.tradingName || '',
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCorporateQuestionnarie.matchFulfilled,
      (state, action: PayloadAction<{ data: Questionnarie }>) => {
        if (state.newApplicationInfo && action?.payload?.data) {
          const { data } = action.payload;

          state.questionnarie = {
            typeOfBusinessLicence: data?.typeOfBusinessLicence || '',
            hmrcMlrNumber: data?.hmrcMlrNumber || '',
            hmrcRegistrationExpiryDate: data?.hmrcRegistrationExpiryDate || '',
            fcaNumber: data?.fcaNumber || '',
            icoNumber: data?.icoNumber || '',
            numberOfBranches: data?.numberOfBranches || '',
            numberOfAgents: data?.numberOfAgents || '',
            mlroDetails: data?.mlroDetails || '',
            complianceConsultantDetails:
              data?.complianceConsultantDetails || '',
            accountantDetails: data?.accountantDetails || '',
            itProviderDetails: data?.itProviderDetails || '',
            payoutPartnerDetails: data.payoutPartnerDetails || '',
            hasTransactionMonitoring: data?.hasTransactionMonitoring?.toString()
              ? data?.hasTransactionMonitoring === false
                ? 'no'
                : 'yes'
              : '',
            transactionMonitoringKind: data?.transactionMonitoringKind || '',
            hasScreening: data?.hasScreening?.toString()
              ? data?.hasScreening === false
                ? 'no'
                : 'yes'
              : '',
            screeningKind: data?.screeningKind || '',
            hasRegulatoryInvestigation:
              data?.hasRegulatoryInvestigation?.toString()
                ? data?.hasRegulatoryInvestigation === false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryInvestigationDetails:
              data?.regulatoryInvestigationDetails || '',
            visitedByAuthorities: data?.visitedByAuthorities || '',
            visitedByAuthoritiesDetails:
              data?.visitedByAuthoritiesDetails || '',
            hasOnboardCorporateClients:
              data?.hasOnboardCorporateClients?.toString()
                ? data?.hasOnboardCorporateClients === false
                  ? 'no'
                  : 'yes'
                : '',
            hasOnboardMsb: data?.hasOnboardMsb?.toString()
              ? data?.hasOnboardMsb === false
                ? 'no'
                : 'yes'
              : '',
            hasPaymentsForCharities: data?.hasPaymentsForCharities?.toString()
              ? data?.hasPaymentsForCharities === false
                ? 'no'
                : 'yes'
              : '',
            hasChangesInManagementOrOwnership:
              data?.hasChangesInManagementOrOwnership?.toString()
                ? data?.hasChangesInManagementOrOwnership === false
                  ? 'no'
                  : 'yes'
                : '',
            changesInManagementOrOwnershipDetails:
              data?.visitedByAuthoritiesDetails || '',
            hasWorkingWithAnyOtherIpsp:
              data?.hasWorkingWithAnyOtherIpsp?.toString()
                ? data?.hasWorkingWithAnyOtherIpsp === false
                  ? 'no'
                  : 'yes'
                : '',
            workingWithAnyOtherIpspDetails:
              data?.workingWithAnyOtherIpspDetails || '',
            hasAuditedByExternalCompliance:
              data?.hasAuditedByExternalCompliance?.toString()
                ? data?.hasAuditedByExternalCompliance === false
                  ? 'no'
                  : 'yes'
                : '',
            auditedByExternalComplianceDetails:
              data?.auditedByExternalComplianceDetails || '',
            contactPersonName: data?.contactPersonName || '',
            phoneNumber: data?.phoneNumber || '',
            faxNumber: data?.faxNumber || '',
            contactEmail: data?.contactEmail || '',
            legalEntityIdCode: data?.legalEntityIdCode || '',
            isPepOrAssociate:
              data?.isPepOrAssociate?.toString() &&
              data?.isPepOrAssociate?.toString() !== 'null'
                ? data?.isPepOrAssociate === false
                  ? 'no'
                  : 'yes'
                : 'yes',
            nameOfPep: data?.nameOfPep || '',
            relToCustomerId: data?.relToCustomerId || '',
            relToPep: data?.relToPep || '',
            orgEmployerAndPosition: data?.orgEmployerAndPosition || '',
            positionHeldCountryId: data?.positionHeldCountryId || '',
            politicalFunctionPeriodFrom:
              data?.politicalFunctionPeriodFrom || '',
            politicalFunctionPeriodTo: data?.politicalFunctionPeriodTo || '',
            abilityMoveGovernmentFunds:
              data?.abilityMoveGovernmentFunds?.toString() &&
              data?.abilityMoveGovernmentFunds?.toString() !== 'null'
                ? data?.abilityMoveGovernmentFunds === false
                  ? 'no'
                  : 'yes'
                : '',
            abilityMoveGovernmentFundsDetails:
              data?.abilityMoveGovernmentFundsDetails || '',
            belongsToGroup:
              data?.belongsToGroup?.toString() &&
              data?.belongsToGroup?.toString() !== 'null'
                ? data?.belongsToGroup === false
                  ? 'no'
                  : 'yes'
                : '',
            parentCompanyName: data?.parentCompanyName || '',
            parentCompanyCountryId: data?.parentCompanyCountryId || '',
            subsidiariesInfo: data?.subsidiariesInfo || '',
            mainActivities: data?.mainActivities || '',
            groupWealthSourceIds: data?.groupWealthSourceIds || [],
            groupWealthSourceOther: data?.groupWealthSourceOther || '',
            estimatedTotalWealthEuro:
              data?.estimatedTotalWealthEuro ||
              data?.estimatedTotalWealthEuro?.toString() === '0'
                ? data?.estimatedTotalWealthEuro?.toString()
                : '',
            customerAppliesKycProcedures:
              data?.customerAppliesKycProcedures?.toString() &&
              data?.customerAppliesKycProcedures?.toString() !== 'null'
                ? data?.customerAppliesKycProcedures === false
                  ? 'no'
                  : 'yes'
                : '',
            hasBusinessActivity:
              data?.hasBusinessActivity?.toString() &&
              data?.hasBusinessActivity?.toString() !== 'null'
                ? data?.hasBusinessActivity === false
                  ? 'no'
                  : 'yes'
                : '',
            businessActivitiesIds:
              data?.hasBusinessActivity?.toString() &&
              data?.hasBusinessActivity?.toString() !== 'null' &&
              data?.hasBusinessActivity === false
                ? ['NONE']
                : data?.businessActivitiesIds,
            customersMainClientsIds: data?.customersMainClientsIds || [],
            customersMainClientsOther: data?.customersMainClientsOther || '',
            salesChannelsIds: data?.salesChannelsIds || [],
            salesChannelsOther: data?.salesChannelsOther || '',
            numberOfEmployees:
              data?.numberOfEmployees ||
              data?.numberOfEmployees?.toString() === '0'
                ? data?.numberOfEmployees.toString()
                : '',
            hasAccountsInOtherFinancialInstitutions:
              data?.hasAccountsInOtherFinancialInstitutions?.toString() &&
              data?.hasAccountsInOtherFinancialInstitutions?.toString() !==
                'null'
                ? data?.hasAccountsInOtherFinancialInstitutions === false
                  ? 'no'
                  : 'yes'
                : '',
            otherFinancialInstitutions: data?.otherFinancialInstitutions || '',
            sourceOfWealthIds: data?.sourceOfWealthIds || [],
            sourceOfWealthOther: data?.sourceOfWealthOther,
            sourcesOfFundsIds: data?.sourcesOfFundsIds || [],
            sourcesOfFundsLoanBusinessPartner:
              data?.sourcesOfFundsLoanBusinessPartner || '',
            sourcesOfFundsLoanCreditInstitution:
              data?.sourcesOfFundsLoanCreditInstitution || '',
            sourcesOfFundsOther: data?.sourcesOfFundsOther || '',
            estimatedTotalWealth:
              data?.estimatedTotalWealth ||
              data?.estimatedTotalWealth?.toString() === '0'
                ? data?.estimatedTotalWealth?.toString()
                : '',
            annualIncome:
              data?.annualIncome || data?.annualIncome?.toString() === '0'
                ? data?.annualIncome?.toString()
                : '',
            sourcesOfAnnualIncomes: Array.isArray(data?.sourcesOfAnnualIncomes)
              ? data?.sourcesOfAnnualIncomes
              : [],
            sourcesOfAnnualIncomesOther:
              data?.sourcesOfAnnualIncomesOther || '',
            accountTypeIds: data?.accountTypeIds || [],
            tradingName: data?.tradingName || '',
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCorporateApplication.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const { data } = action.payload;

        const nextInfo = data;

        const similarApplications = nextInfo?.similarApplications?.edges.map(
          (item) => ({
            node: {
              id: item.node.id,
              firstName: item.node.firstName || '',
              lastName: item.node.lastName || '',
              status: item.node.status,
              userId: item.node.userId || '',
              isClient: item.node.isClient,
              type: item.node.type,
              companyName: item.node.companyName || '',
              selected: Boolean(
                nextInfo.reviewedDuplicates.find(
                  (duplicate) => item.node.id === duplicate,
                ),
              ),
            },
          }),
        );

        if (nextInfo?.relatedApplications?.length) {
          const mainRepresentativeApplications =
            nextInfo?.relatedApplications?.filter(
              (item) =>
                item.companyOfficialType ===
                ApplicationType.MAIN_REPRESENTATIVE,
            );

          const companyOfficialsApplications =
            nextInfo?.relatedApplications?.filter(
              (item) =>
                item.companyOfficialType === ApplicationType.COMPANY_OFFICIAL,
            );

          if (mainRepresentativeApplications) {
            state.mainRepresentativeApplications =
              mainRepresentativeApplications.map((item) => item);
          }

          if (companyOfficialsApplications) {
            state.companyOfficialsApplications =
              companyOfficialsApplications?.map((item) => item);
          }
        }
        state.newApplicationInfo = {
          ...nextInfo,
          similarApplications: {
            edges: similarApplications as { node: Match }[],
          },
        };
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCorporateApplicationStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = {
          ...state.newApplicationInfo,
          ...data,
        };

        state.newApplicationInfo = nextNewApplicationInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCorporateApplicationSystemLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<SystemLogs>>) => {
        state.systemLogs = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCorporateReviewStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = { ...state.newApplicationInfo };

        nextNewApplicationInfo.reviewStatus = data.reviewStatus;

        state.newApplicationInfo =
          nextNewApplicationInfo as NewApplicationCorporateInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.addNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = ([] as { node: ApplicationComment }[]).concat(
            nextNewApplicationInfo?.comments?.edges as {
              node: ApplicationComment;
            }[],
          );

          nextComments.push({ node: { ...data } });

          nextNewApplicationInfo.comments = { edges: nextComments };
          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = nextNewApplicationInfo?.comments?.edges.map(
            (item) => {
              let result = { ...item };

              if (result.node.id === data.id) {
                result = { node: data };
              }

              return result;
            },
          );

          nextNewApplicationInfo.comments = {
            edges: nextComments as { node: ApplicationComment }[],
          };

          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = nextNewApplicationInfo?.comments?.edges.filter(
            (item) => item.node.id !== data.id,
          );

          nextNewApplicationInfo.comments = {
            edges: nextComments as { node: ApplicationComment }[],
          };
          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCorporateNewApplicationDocuments
        .matchFulfilled,
      (state, action: PayloadAction<Response<ApplicationDocument>>) => {
        state.documents = action.payload.data.edges.map((item) => item.node);

        const stepsStatus = {
          ...state.stepsStatus,
          stepSix: getStepSixStatus(
            action.payload.data.edges.map((item) => item.node),
          ),
        };

        state.stepsStatus = stepsStatus as NewApplicationCorporateSteps;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCorporateNewApplicationDocument
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        state.document = {
          ...action.payload.data,
          commentsList: action.payload.data.comments?.edges.map(
            (item) => item.node,
          ),
        };
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.addCorporateApplicationDocuments.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        const nextDocuments = ([] as ApplicationDocument[]).concat(
          state.documents as ApplicationDocument[],
        );

        nextDocuments.push(action.payload.data);

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.addCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          nextComments.unshift(action.payload.data);
          nextDocument.commentsList = nextComments;

          const nextDocuments = state.documents?.map((item) => {
            const result = { ...item };

            if (result.id === state?.document?.id) {
              result.countComments = {
                count: (result.countComments?.count || 0) + 1,
              };
            }

            return result;
          });

          state.documents = nextDocuments;
          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.editCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.deleteCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };

          nextDocument.commentsList = nextDocument?.commentsList?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };

            if (result.id === state?.document?.id) {
              result.countComments = {
                count: (result.countComments?.count as number) - 1,
              };
            }

            return result;
          });

          state.documents = nextDocuments;
          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.changeCorporateApplicationDocumentStatus
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: ApplicationDocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        const { ids, status } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.status = status;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.status = status;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.changeNewApplicationDocumentExpirationDate
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        if (state.document) {
          const nextDocument = { ...state?.document };

          nextDocument.expirationDate = action.payload.data.expirationDate;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.assignCompanyOfficial.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationChildrenInfo }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = { ...state.newApplicationInfo };

        if (data.companyOfficialType === ApplicationType.COMPANY_OFFICIAL) {
          let nextCompanyOfficials: NewApplicationChildrenInfo[] = [];

          if (state.companyOfficialsApplications) {
            nextCompanyOfficials = ([] as NewApplicationChildrenInfo[]).concat(
              state.companyOfficialsApplications,
            );
          }

          if (!nextCompanyOfficials.find((item) => item.id === data.id)) {
            nextCompanyOfficials.push(data);
          }

          if (
            state.mainRepresentativeApplications &&
            state.mainRepresentativeApplications.find(
              (item) => item.id === data.id,
            )
          ) {
            state.mainRepresentativeApplications =
              state.mainRepresentativeApplications.filter(
                (item) => item.id !== data.id,
              );
          }

          state.companyOfficialsApplications = nextCompanyOfficials;
        } else if (
          data.companyOfficialType === ApplicationType.MAIN_REPRESENTATIVE
        ) {
          let nextMainRepresentative: NewApplicationChildrenInfo[] = [];

          if (state.mainRepresentativeApplications) {
            nextMainRepresentative = (
              [] as NewApplicationChildrenInfo[]
            ).concat(state.mainRepresentativeApplications);
          }

          if (!nextMainRepresentative.find((item) => item.id === data.id)) {
            nextMainRepresentative.push(data);
          }

          if (
            state.companyOfficialsApplications &&
            state.companyOfficialsApplications.find(
              (item) => item.id === data.id,
            )
          ) {
            state.companyOfficialsApplications =
              state.companyOfficialsApplications.filter(
                (item) => item.id !== data.id,
              );
          }

          state.mainRepresentativeApplications = nextMainRepresentative;
        }

        state.newApplicationInfo =
          nextNewApplicationInfo as NewApplicationCorporateInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateAppToAppStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationChildrenInfo }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const currentMR = state.mainRepresentativeApplications?.find(
            (item) => item.id === data.id,
          );
          const currentCOF = state.companyOfficialsApplications?.find(
            (item) => item.id === data.id,
          );
          const currentRelatedApps =
            nextNewApplicationInfo.relatedApplications?.find(
              (item) => item.id === data.id,
            );

          if (currentMR) {
            state.mainRepresentativeApplications =
              state.mainRepresentativeApplications?.map((item) => {
                let result = { ...item };

                if (item.id === data.id) {
                  result = data;
                }

                return result;
              });
          }

          if (currentCOF) {
            state.companyOfficialsApplications =
              state.companyOfficialsApplications?.map((item) => {
                let result = { ...item };

                if (item.id === data.id) {
                  result = data;
                }

                return result;
              });
          }

          if (currentRelatedApps) {
            nextNewApplicationInfo.relatedApplications =
              nextNewApplicationInfo.relatedApplications?.map((item) => {
                let result = { ...item };

                if (item.id === data.id) {
                  result = data;
                }

                return result;
              });
          }

          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAgreements.matchFulfilled,
      (state, action: PayloadAction<Response<ApplicationAgreement>>) => {
        state.agreements = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(
            ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_AGREEMENT,
            {
              applicationId: (state.applicationId as string) || '',
              agreementId: item.node.id,
            },
          ),
        }));
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.setReviewEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = {
          ...state.newApplicationInfo,
          ...data,
        };

        state.newApplicationInfo =
          nextNewApplicationInfo as NewApplicationCorporateInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingMonitoring.matchFulfilled,
      (state, action: PayloadAction<Response<OngoingMonitring>>) => {
        if (state.newApplicationInfo) {
          const items = action?.payload?.data?.edges?.map((item) => item.node);

          const sorted = items?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          state.ongoingMonitoring = [sorted[0]];
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.resetApplicationWarningAction.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = {
                node: {
                  ...action.payload.data,
                  action: undefined,
                  matchStatus: undefined,
                  comment: undefined,
                },
              };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.ongoingMonitoring = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.applyApplicationOngoingWarningAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = { node: action.payload.data };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.ongoingMonitoring = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.batchApplyApplicationOngoingWarningAction
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  action: string;
                  comment: string;
                  matchStatus?: string;
                };
              };
            };
          };
        },
      ) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map(
          (item: OngoingMonitring) => {
            const result = { ...item };

            const nextWarnings = result?.warnings?.edges?.map(
              (warning: { node: TransactionWarning }) => {
                let resultWarning = { ...warning };

                resultWarning = {
                  node: {
                    ...resultWarning.node,
                    action: action.meta.arg.originalArgs.body
                      .action as TransactionWarningActionEnum,
                    comment: action.meta.arg.originalArgs.body.comment,
                    matchStatus: action.meta.arg.originalArgs.body.matchStatus,
                  },
                };

                return resultWarning;
              },
            );

            result.warnings = {
              edges: nextWarnings,
              pageInfo: result?.warnings?.pageInfo,
            };

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingStatus.matchFulfilled,
      (state, action: PayloadAction<Response<OngoingCheckingStatus>>) => {
        if (state.newApplicationInfo) {
          const items = action?.payload?.data?.edges?.map((item) => item.node);

          const sorted = items?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          const [sortedValue] = sorted;

          state.ongoingCheckingStatus = sortedValue;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.restartOngoingChecking.matchFulfilled,
      (state, action: PayloadAction<{ data: OngoingCheckingStatus }>) => {
        if (state.newApplicationInfo) {
          state.ongoingCheckingStatus = action?.payload?.data;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.renameCorporateDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        if (state.documents) {
          const nextDocuments = state.documents?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.name = action.payload.data.name;
            }

            return result;
          });

          state.documents = nextDocuments;
        }

        if (state.document && state.document.id === action.payload.data.id) {
          const nextDocument = { ...state.document };

          nextDocument.name = action.payload.data.name;

          state.document = nextDocument;
        }
      },
    );
  },
});

export const {
  onSelectAllGroup,
  checkChatMessages,
  closeChatMessageHint,
  clearNewApplicationInfoState,
  rejectApplication,
  setDocumentSearchText,
  setHintVisible,
  setMatchSelected,
  onGroupSelected,
  onSelectDocument,
  updateStepsStatus,
  clearDocumentSidepanelState,
  deleteIsSuccess,
  onDocumentMove,
  updateMainRepresentativeValues,
  updateCompanyOfficialValues,
  clearCofAndMRState,
} = newAppliationCorporateInfoSlice.actions;

export default newAppliationCorporateInfoSlice.reducer;
